import AccountCircle from "@mui/icons-material/AccountCircle";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Circle from "@mui/icons-material/Circle";
import Close from "@mui/icons-material/Close";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Star from "@mui/icons-material/Star";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPremiumProps,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsCellItem,
  GridColDef,
  gridDetailPanelExpandedRowIdsSelector,
  GridRowId,
  GridRowParams,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityType as PrismaActivityType } from "encamp-prisma/generated/client";
import { gql } from "generated-graphql";
import {
  ActivityType,
  FacilityPilotProgramReadyStatus,
  FacilityPilotProgramStatesQuery,
  FacilityPilotProgramStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useLinearIssueStatuses } from "hooks/useLinearIssueStatuses";
import pluralize from "pluralize";
import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { AddPilotFacilityModal } from "./AddPilotFacilityModal";
import { DetailPanelContent } from "./DetailPanelContent";
import { EditPilotProgramModal } from "./EditPilotProgramModal";
import { PilotWorkflowTagsCell } from "./PilotWorkflowTagsCell";

const FACILITY_PILOT_PROGRAM_STATES_QUERY = gql(`
  query FacilityPilotProgramStates($reportingYear: Int!) {
    facilityPilotProgramStates(reportingYear: $reportingYear) {
      items {
        state
        stateName
        verifiedReportCount
        pilotFacilityCount
        totalReportingFacilities
        status
        isStateEnabled
        portalTech
        readyStatus
        notes
        documentationComplete
        qaComplete
        pilotFacilities {
          facilityId
          facilityName
          streetAddress1
          city
          facilityState
          tenantId
          tenantName
          isDeniedTenant
          reportId
          reportOrgStatus
          reportEncampStatus
          workflowStatus
          workflowId
          workflowTags {
            id
            name
          }
          configId
          configNotes
          configCreatedAt
          configCreatedBy
          currentActivityType
          currentActivityDisplayName
        }
      }
    }
  }
`);

const REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION = gql(`
  mutation RemoveFacilityFromPilotProgram($configId: ID!) {
    removeFacilityFromPilotProgram(configId: $configId)
  }
`);

const UPDATE_WORKFLOW_TAGS_BULK_MUTATION = gql(`
  mutation UpdateWorkflowTagsBulk($workflowUpdates: [WorkflowTagUpdateInput!]!) {
    updateWorkflowTagsBulk(workflowUpdates: $workflowUpdates) {
      id
      tags {
        id
        name
      }
    }
  }
`);

type StateRow =
  FacilityPilotProgramStatesQuery["facilityPilotProgramStates"]["items"][number];
type PilotFacilityRow = StateRow["pilotFacilities"][number];

const getStatusLabel = (status: FacilityPilotProgramStatus): string => {
  switch (status) {
    case FacilityPilotProgramStatus.NotStarted:
      return "Not Started";
    case FacilityPilotProgramStatus.InError:
      return "Error";
    case FacilityPilotProgramStatus.InProgress:
      return "In Progress";
    case FacilityPilotProgramStatus.Completed:
      return "Completed";
    default:
      return status;
  }
};

// Compute unique tags from all workflows in a state
const getStateTags = (row: StateRow): string[] => {
  const allTags = new Set<string>();
  row.pilotFacilities.forEach((facility) => {
    facility.workflowTags?.forEach((tag) => {
      allTags.add(tag.name);
    });
  });
  return Array.from(allTags).sort();
};

// Get status icon component
const getStatusIcon = (
  status: FacilityPilotProgramStatus,
  isStateEnabled: boolean
) => {
  // Allowed state takes precedence over all other statuses
  if (isStateEnabled) {
    return <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />;
  }

  switch (status) {
    case FacilityPilotProgramStatus.Completed:
      return (
        <CheckCircle
          fontSize="small"
          sx={{ color: "success.light", opacity: 0.8 }}
        />
      );
    case FacilityPilotProgramStatus.InError:
      return (
        <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.InProgress:
      return (
        <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.NotStarted:
    default:
      return (
        <Circle
          fontSize="small"
          sx={{ color: "text.disabled", opacity: 0.6 }}
        />
      );
  }
};

// Get status icon component without star (for checklist column)
const getStatusIconWithoutStar = (status: FacilityPilotProgramStatus) => {
  switch (status) {
    case FacilityPilotProgramStatus.Completed:
      return (
        <CheckCircle
          fontSize="small"
          sx={{ color: "success.light", opacity: 0.8 }}
        />
      );
    case FacilityPilotProgramStatus.InError:
      return (
        <Error fontSize="small" sx={{ color: "error.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.InProgress:
      return (
        <Info fontSize="small" sx={{ color: "info.light", opacity: 0.8 }} />
      );
    case FacilityPilotProgramStatus.NotStarted:
    default:
      return (
        <Circle
          fontSize="small"
          sx={{ color: "text.disabled", opacity: 0.6 }}
        />
      );
  }
};

const ExpandCollapseAllHeader = () => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);

  const expandableRowIds = useMemo(() => {
    return Object.keys(rowsLookup).filter((rowId) => {
      const row = rowsLookup[rowId] as StateRow;
      return row.pilotFacilities && row.pilotFacilities.length > 0;
    });
  }, [rowsLookup]);

  const allExpanded =
    expandableRowIds.length > 0 &&
    expandableRowIds.every((id) => expandedRowIds.has(id));

  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      expandedRowIds.forEach((rowId: GridRowId) => {
        apiRef.current.toggleDetailPanel(rowId);
      });
    } else {
      expandableRowIds.forEach((rowId) => {
        if (!expandedRowIds.has(rowId)) {
          apiRef.current.toggleDetailPanel(rowId);
        }
      });
    }
  };

  return (
    <IconButton
      onClick={handleExpandCollapseAll}
      size="small"
      title={allExpanded ? "Collapse all" : "Expand all"}
    >
      {allExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

const ExpandCollapseCell = ({ row, id }: { row: StateRow; id: GridRowId }) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const isExpanded = expandedRowIds.has(id);
  const hasPilotFacilities =
    row.pilotFacilities && row.pilotFacilities.length > 0;

  if (!hasPilotFacilities) {
    return null;
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        apiRef.current.toggleDetailPanel(id);
      }}
    >
      {isExpanded ? (
        <KeyboardArrowDown fontSize="small" />
      ) : (
        <KeyboardArrowRight fontSize="small" />
      )}
    </IconButton>
  );
};

export function FacilityPilotProgram() {
  const [reportingYear, setReportingYear] = useState<number>(
    currentTierIIReportingYear
  );
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<StateRow | null>(null);
  const [editingRow, setEditingRow] = useState<StateRow | null>(null);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    FacilityPilotProgramStatus | "enabled" | "notEnabled" | "started" | null
  >(null);

  // Handle status filter clicks - toggle filter if same, set if different
  // For "enabled" filter, cycle through: null → "enabled" → "notEnabled" → null
  const handleStatusFilterClick = useCallback(
    (filter: FacilityPilotProgramStatus | "enabled" | "notEnabled" | "started" | null) => {
      if (filter === "enabled") {
        // Cycle through: null → "enabled" → "notEnabled" → null
        setStatusFilter((current) => {
          if (current === null) return "enabled";
          if (current === "enabled") return "notEnabled";
          if (current === "notEnabled") return null;
          return "enabled"; // If something else is set, go to enabled
        });
      } else {
        // For other filters, toggle on/off
        setStatusFilter((current) => (current === filter ? null : filter));
      }
    },
    []
  );
  const alerts = useAlerts();
  const apiRef = useGridApiRef();

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Facility Pilot Program",
    },
  ]);

  const { data, loading, refetch } = useQuery(
    FACILITY_PILOT_PROGRAM_STATES_QUERY,
    {
      variables: { reportingYear },
      fetchPolicy: "cache-and-network",
    }
  );

  const [removeMutation, { loading: removeLoading }] = useMutation(
    REMOVE_FACILITY_FROM_PILOT_PROGRAM_MUTATION,
    {
      onCompleted: () => {
        alerts.success("Facility removed from pilot program");
        refetch();
      },
      onError: (error) => {
        alerts.error(`Failed to remove facility: ${error.message}`);
      },
    }
  );

  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [FACILITY_PILOT_PROGRAM_STATES_QUERY],
    }
  );

  const handleRemoveFacility = useCallback(
    (configId: string) => {
      if (
        window.confirm(
          "Are you sure you want to remove this facility from the pilot program?"
        )
      ) {
        removeMutation({ variables: { configId } });
      }
    },
    [removeMutation]
  );

  const handleAddFacility = useCallback((row: StateRow) => {
    setSelectedRow(row);
    setAddModalOpen(true);
  }, []);

  const handleEditProgram = useCallback((row: StateRow) => {
    setEditingRow(row);
    setEditModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setAddModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    setEditingRow(null);
  }, []);

  const handleFacilityAdded = useCallback(() => {
    refetch();
    handleModalClose();
  }, [refetch, handleModalClose]);

  // Handle opening tags dialog for a state
  const handleOpenTagsDialog = useCallback((row: StateRow) => {
    setSelectedRow(row);
    setTagsDialogOpen(true);
  }, []);

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedRow(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected state workflows to dialog format
  const tagsDialogRows = useMemo(() => {
    if (!selectedRow) return [];
    return selectedRow.pilotFacilities
      .filter(
        (facility): facility is PilotFacilityRow & { workflowId: string } =>
          facility.workflowId !== null
      )
      .map((facility) => ({
        id: facility.workflowId,
        tags: facility.workflowTags?.map((tag) => tag.name) || [],
      }));
  }, [selectedRow]);

  // Get all rows from data (unfiltered for stats calculation)
  const allRows = useMemo(
    () => data?.facilityPilotProgramStates.items ?? [],
    [data]
  );

  // Collect all tags from all rows for Linear issue lookup (call API once for entire datagrid)
  // This must be defined early so it's available for columns and getDetailPanelContent
  const allTagsForLinear = useMemo(() => {
    const tagSet = new Set<string>();
    allRows.forEach((row) => {
      const stateTags = getStateTags(row);
      stateTags.forEach((tag) => tagSet.add(tag));
      // Also collect tags from individual facilities in sub-datagrid
      row.pilotFacilities.forEach((facility) => {
        facility.workflowTags?.forEach((tag) => {
          tagSet.add(tag.name);
        });
      });
    });
    return Array.from(tagSet);
  }, [allRows]);

  const { statusMap: linearStatusMap } =
    useLinearIssueStatuses(allTagsForLinear);

  // Define columns after linearStatusMap is available so it can be included in dependencies
  const columns: GridColDef<StateRow>[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderHeader: () => <ExpandCollapseAllHeader />,
        renderCell: ({ row, id }) => <ExpandCollapseCell row={row} id={id} />,
      },
      {
        field: "statusIcon",
        headerName: "State Enabled",
        width: 80,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            State Enabled
          </Typography>
        ),
        renderCell: ({ row }) => {
          if (row.isStateEnabled) {
            return (
              <Tooltip title="State is enabled">
                <Star fontSize="small" sx={{ color: "#FFD700", opacity: 0.9 }} />
              </Tooltip>
            );
          }
          return null;
        },
      },
      {
        field: "checklist",
        headerName: "Checklist",
        width: 90,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return (
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              {/* Left dot: Pilot Status */}
              <Tooltip title={getStatusLabel(row.status)}>
                {row.readyStatus === FacilityPilotProgramReadyStatus.Ready ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  getStatusIconWithoutStar(row.status)
                )}
              </Tooltip>
              
              {/* Middle dot: QA Complete */}
              <Tooltip title={row.qaComplete ? "QA is complete" : "QA is not complete"}>
                {row.qaComplete ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  <Circle
                    fontSize="small"
                    sx={{ color: "text.disabled", opacity: 0.6 }}
                  />
                )}
              </Tooltip>
              
              {/* Right dot: Documentation Complete */}
              <Tooltip title={row.documentationComplete ? "Documentation is complete" : "Documentation is not complete"}>
                {row.documentationComplete ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{ color: "success.main", opacity: 0.8 }}
                  />
                ) : (
                  <Circle
                    fontSize="small"
                    sx={{ color: "text.disabled", opacity: 0.6 }}
                  />
                )}
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 60,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="bold">
            {row.state}
          </Typography>
        ),
      },
      {
        field: "stateName",
        headerName: "State Name",
        width: 110,
      },
      {
        field: "portalTech",
        headerName: "Portal Tech",
        width: 100,
        valueGetter: (_, row) => row.portalTech ?? "-",
      },
      {
        field: "totalReportingFacilities",
        headerName: "Reporting Facilities",
        width: 105,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Reporting Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {row.totalReportingFacilities}
          </Typography>
        ),
      },
      {
        field: "verifiedReportCount",
        headerName: "Candidate Facilities",
        width: 90,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Candidate Facilities
          </Typography>
        ),
        renderCell: ({ row }) => (
          <Tooltip title="Reports with encampStatus = PROCESSING">
            <Typography variant="body2">{row.verifiedReportCount}</Typography>
          </Tooltip>
        ),
      },
      {
        field: "pilotFacilityCount",
        headerName: "Pilot Facilities",
        width: 80,
        type: "number",
        renderHeader: () => (
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            Pilot Facilities
          </Typography>
        ),
        renderCell: ({ row }) => {
          const hasStaffActivity = row.pilotFacilities.some(
            (f) => f.currentActivityType === ActivityType.Staff
          );
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {hasStaffActivity && (
                <AccountCircle
                  sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                />
              )}
              <Typography
                variant="body2"
                fontWeight={row.pilotFacilityCount > 0 ? "bold" : "normal"}
              >
                {row.pilotFacilityCount}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "tags",
        headerName: "Pilot Workflow Tags",
        width: 270,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <PilotWorkflowTagsCell
              row={row}
              onOpenTagsDialog={handleOpenTagsDialog}
              linearStatusMap={linearStatusMap}
            />
          );
        },
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 260,
        sortable: false,
        cellClassName: "notes-cell",
        renderCell: ({ row }) => (
          <Tooltip title={row.notes || ""}>
            <Box
              sx={{
                width: "100%",
                fontSize: "0.7rem",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
                wordWrap: "break-word",
                lineHeight: 1.2,
              }}
            >
              {row.notes || ""}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const hasNoCandidateReports = (row.verifiedReportCount ?? 0) === 0;
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {hasNoCandidateReports ? (
                <Tooltip title="No candidate facilities">
                  <span>
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        opacity: 0,
                        transition: "opacity 0.2s",
                        cursor: "not-allowed",
                        ".MuiDataGrid-row:hover &": {
                          opacity: 1,
                        },
                      }}
                    >
                      <DoNotDisturb />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="Add pilot facility">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddFacility(row);
                    }}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      ".MuiDataGrid-row:hover &": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit pilot program">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProgram(row);
                  }}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.2s",
                    ".MuiDataGrid-row:hover &": {
                      opacity: 1,
                    },
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      handleAddFacility,
      handleEditProgram,
      handleOpenTagsDialog,
      linearStatusMap,
    ]
  );

  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelHeight"]>
  >(() => "auto", []);

  const handleRowClick = useCallback(
    (params: GridRowParams<StateRow>) => {
      // Only expand if there are pilot facilities to show
      if (params.row.pilotFacilities && params.row.pilotFacilities.length > 0) {
        apiRef.current?.toggleDetailPanel(params.id);
      }
    },
    [apiRef]
  );

  // Define getDetailPanelContent after linearStatusMap is available
  const getDetailPanelContent = useCallback<
    NonNullable<DataGridPremiumProps["getDetailPanelContent"]>
  >(
    ({ row }) => {
      if (!row.pilotFacilities || row.pilotFacilities.length === 0) {
        return null;
      }
      return (
        <DetailPanelContent
          pilotFacilities={row.pilotFacilities}
          onRemove={handleRemoveFacility}
          removeLoading={removeLoading}
          linearStatusMap={linearStatusMap}
        />
      );
    },
    [handleRemoveFacility, removeLoading, linearStatusMap]
  );

  // Filter rows: exclude states with 0 reporting facilities and apply status filter
  const rows = useMemo(() => {
    let filtered = allRows.filter((row) => row.totalReportingFacilities > 0);

    // Apply status filter if active
    if (statusFilter !== null) {
      if (statusFilter === "enabled") {
        filtered = filtered.filter((row) => row.isStateEnabled);
      } else if (statusFilter === "notEnabled") {
        filtered = filtered.filter((row) => !row.isStateEnabled);
      } else if (statusFilter === "started") {
        filtered = filtered.filter(
          (row) => row.status !== FacilityPilotProgramStatus.NotStarted
        );
      } else {
        filtered = filtered.filter((row) => row.status === statusFilter);
      }
    }

    return filtered;
  }, [allRows, statusFilter]);

  // Collect all existing pilot facility IDs for the modal (use allRows to include all states)
  const existingPilotFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    allRows.forEach((row) => {
      row.pilotFacilities.forEach((pf) => {
        ids.add(pf.facilityId);
      });
    });
    return ids;
  }, [allRows]);

  // Calculate summary statistics - exclude states with 0 reporting facilities (same as grid filter)
  const summaryStats = useMemo(() => {
    // Filter out states with 0 reporting facilities (same filter as the grid)
    const filteredRows = allRows.filter(
      (row) => row.totalReportingFacilities > 0
    );

    if (filteredRows.length === 0) {
      return {
        totalStates: 0,
        statesEnabled: 0,
        statesEnabledPercent: 0,
        statesStarted: 0,
        statesStartedPercent: 0,
        statesCompleted: 0,
        statesCompletedPercent: 0,
        statesInError: 0,
        statesInErrorPercent: 0,
        statesNotStarted: 0,
        statesNotStartedPercent: 0,
        totalPilotFacilities: 0,
        totalVerifiedReports: 0,
        pilotFacilitiesWaitingForStaff: 0,
        readyToStartStates: 0,
        statesWithNoCandidates: 0,
        statesCanBeEnabled: 0,
      };
    }

    const totalStates = filteredRows.length;
    const statesEnabled = filteredRows.filter((r) => r.isStateEnabled).length;
    const statesStarted = filteredRows.filter(
      (r) => r.status !== FacilityPilotProgramStatus.NotStarted
    ).length;
    const statesCompleted = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.Completed
    ).length;
    const statesInError = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.InError
    ).length;
    const statesNotStarted = filteredRows.filter(
      (r) => r.status === FacilityPilotProgramStatus.NotStarted
    ).length;
    const totalPilotFacilities = filteredRows.reduce(
      (acc, r) => acc + r.pilotFacilityCount,
      0
    );
    const totalVerifiedReports = filteredRows.reduce(
      (acc, r) => acc + r.verifiedReportCount,
      0
    );

    // Count pilot facilities waiting for staff (where currentActivityType === ActivityType.Staff)
    const pilotFacilitiesWaitingForStaff = filteredRows.reduce((acc, r) => {
      return (
        acc +
        r.pilotFacilities.filter(
          (pf) => pf.currentActivityType === ActivityType.Staff
        ).length
      );
    }, 0);

    // Count ready-to-start states (status === NotStarted and verifiedReportCount > 0)
    const readyToStartStates = filteredRows.filter(
      (r) =>
        r.status === FacilityPilotProgramStatus.NotStarted &&
        (r.verifiedReportCount ?? 0) > 0
    ).length;

    // Count states with no candidates (verifiedReportCount === 0)
    const statesWithNoCandidates = filteredRows.filter(
      (r) => (r.verifiedReportCount ?? 0) === 0
    ).length;

    // Count states that can be enabled (completed pilot facility OR state ready, documentation complete, qa complete)
    // Exclude states that are already enabled
    const statesCanBeEnabled = filteredRows.filter(
      (r) =>
        !r.isStateEnabled && // Exclude already enabled states
        (r.status === FacilityPilotProgramStatus.Completed ||
          r.readyStatus === FacilityPilotProgramReadyStatus.Ready) &&
        r.documentationComplete === true &&
        r.qaComplete === true
    ).length;

    return {
      totalStates,
      statesEnabled,
      statesEnabledPercent: Math.round((statesEnabled / totalStates) * 100),
      statesStarted,
      statesStartedPercent: Math.round((statesStarted / totalStates) * 100),
      statesCompleted,
      statesCompletedPercent: Math.round((statesCompleted / totalStates) * 100),
      statesInError,
      statesInErrorPercent: Math.round((statesInError / totalStates) * 100),
      statesNotStarted,
      statesNotStartedPercent: Math.round(
        (statesNotStarted / totalStates) * 100
      ),
      totalPilotFacilities,
      totalVerifiedReports,
      pilotFacilitiesWaitingForStaff,
      readyToStartStates,
      statesWithNoCandidates,
      statesCanBeEnabled,
    };
  }, [allRows]);

  return (
    <Box sx={{ pt: 3, px: 3 }}>
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={2}
      >
        <TierIIYearPicker
          value={reportingYear}
          onChange={(value) => {
            if (value !== null && value !== undefined) {
              const year =
                typeof value === "number" ? value : parseInt(String(value), 10);
              if (!isNaN(year)) {
                setReportingYear(year);
              }
            }
          }}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="stretch"
          flexWrap="wrap"
          rowGap={1}
        >
          {/* States Enabled mini-stat */}
          <Tooltip
            title={
              statusFilter === "enabled"
                ? "Showing only enabled states"
                : statusFilter === "notEnabled"
                ? "Showing only not enabled states"
                : "Click to filter: Show enabled → Show not enabled → Show all"
            }
          >
            <Paper
              variant="outlined"
              onClick={() => handleStatusFilterClick("enabled")}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: (statusFilter === "enabled" || statusFilter === "notEnabled")
                  ? "#FFD700"
                  : "transparent",
                bgcolor: (statusFilter === "enabled" || statusFilter === "notEnabled")
                  ? "action.hover"
                  : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Star
                  fontSize="small"
                  sx={{ color: "#FFD700", opacity: 0.9 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {statusFilter === "enabled"
                    ? "States Enabled"
                    : statusFilter === "notEnabled"
                    ? "States Not Enabled"
                    : "States Enabled"}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: "#FFD700" }}
                >
                  {summaryStats.statesEnabled}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesEnabledPercent}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#FFD700",
                  },
                }}
              />
            </Paper>
          </Tooltip>

          {/* States Started mini-stat */}
          <Tooltip title="States that are not in 'Not Started' status">
            <Paper
              variant="outlined"
              onClick={() => handleStatusFilterClick("started")}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: statusFilter === "started" ? "info.main" : "transparent",
                bgcolor: statusFilter === "started" ? "action.hover" : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Info
                  fontSize="small"
                  sx={{ color: "info.light", opacity: 0.8 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Started
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="info.main">
                  {summaryStats.statesStarted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesStartedPercent}
                color="info"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States In Error mini-stat */}
          <Tooltip title="States with pilot facilities in error state">
            <Paper
              variant="outlined"
              onClick={() =>
                handleStatusFilterClick(FacilityPilotProgramStatus.InError)
              }
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: statusFilter === FacilityPilotProgramStatus.InError
                  ? "error.main"
                  : "transparent",
                bgcolor: statusFilter === FacilityPilotProgramStatus.InError
                  ? "action.hover"
                  : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Error
                  fontSize="small"
                  sx={{ color: "error.light", opacity: 0.8 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  In Error
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {summaryStats.statesInError}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesInErrorPercent}
                color="error"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Completed mini-stat */}
          <Tooltip title="States where at least one pilot facility has a completed workflow">
            <Paper
              variant="outlined"
              onClick={() =>
                handleStatusFilterClick(FacilityPilotProgramStatus.Completed)
              }
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: statusFilter === FacilityPilotProgramStatus.Completed
                  ? "success.main"
                  : "transparent",
                bgcolor: statusFilter === FacilityPilotProgramStatus.Completed
                  ? "action.hover"
                  : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CheckCircle
                  fontSize="small"
                  sx={{ color: "success.light", opacity: 0.8 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Completed
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {summaryStats.statesCompleted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesCompletedPercent}
                color="success"
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Paper>
          </Tooltip>

          {/* States Not Started mini-stat */}
          <Tooltip title="States that have not started the pilot program">
            <Paper
              variant="outlined"
              onClick={() =>
                handleStatusFilterClick(FacilityPilotProgramStatus.NotStarted)
              }
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                cursor: "pointer",
                transition: "all 0.2s",
                borderWidth: 2,
                borderColor: statusFilter === FacilityPilotProgramStatus.NotStarted
                  ? "text.disabled"
                  : "transparent",
                bgcolor: statusFilter === FacilityPilotProgramStatus.NotStarted
                  ? "action.hover"
                  : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Circle
                  fontSize="small"
                  sx={{ color: "text.disabled", opacity: 0.6 }}
                />
                <Typography variant="caption" color="text.secondary" noWrap>
                  Not Started
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {summaryStats.statesNotStarted}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {summaryStats.totalStates}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={summaryStats.statesNotStartedPercent}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "text.disabled",
                  },
                }}
              />
            </Paper>
          </Tooltip>

          {/* Totals summary */}
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 0.75,
              minWidth: 140,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 0.25,
              bgcolor: "action.hover",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {summaryStats.pilotFacilitiesWaitingForStaff}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                pilot facilities waiting for Staff
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {summaryStats.readyToStartStates}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                states can be started
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" fontWeight="medium">
                {summaryStats.statesCanBeEnabled}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                states can be enabled
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      <Box sx={{ height: "calc(100vh - 280px)", minHeight: 400 }}>
        <DataGrid<StateRow>
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.state}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          onRowClick={handleRowClick}
          disableRowGrouping={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "totalReportingFacilities", sort: "desc" }],
            },
          }}
          pageSizeOptions={[25, 51, 100]}
          sx={{
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-expanded": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-cell.notes-cell": {
              whiteSpace: "normal",
              alignItems: "center",
            },
          }}
        />
      </Box>

      <AddPilotFacilityModal
        open={addModalOpen}
        onClose={handleModalClose}
        onFacilityAdded={handleFacilityAdded}
        state={selectedRow?.state ?? null}
        reportingYear={reportingYear}
        existingPilotFacilityIds={existingPilotFacilityIds}
      />

      <EditPilotProgramModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        onSave={handleFacilityAdded}
        state={editingRow?.state ?? ""}
        reportingYear={reportingYear}
      />

      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="workflow-recent-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />
    </Box>
  );
}
